'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'ar';
type Direction = 'ltr' | 'rtl';

interface LanguageContextType {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = 'embassy_language_preference';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');
  const [direction, setDirection] = useState<Direction>('ltr');

  // Load language preference from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language;
    if (savedLanguage === 'ar' || savedLanguage === 'en') {
      setLanguageState(savedLanguage);
      setDirection(savedLanguage === 'ar' ? 'rtl' : 'ltr');
    }
  }, []);

  // Update direction and save preference when language changes
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    setDirection(lang === 'ar' ? 'rtl' : 'ltr');
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    
    // Update document direction
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  // Translation function (placeholder - will be replaced with actual translations)
  const t = (key: string): string => {
    return translations[language]?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, direction, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Translation dictionary
const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    'nav.home': 'Home',
    'nav.ambassador': 'Ambassador',
    'nav.news': 'News',
    'nav.forms': 'Forms',
    'nav.contact': 'Contact',
    'nav.admin': 'Admin',
    'header.embassy': 'Embassy of Lebanon',
    'header.location': 'State of Kuwait',
    'header.emergency': 'Emergency Services',
    'header.search': 'Search...',
    
    // Homepage
    'home.hero.badge': 'Official Government Portal',
    'home.hero.title': 'Embassy of Lebanon',
    'home.hero.titleHighlight': 'in Kuwait',
    'home.hero.description': 'Serving the Lebanese community with dedication and excellence. Your trusted partner for consular services, documentation, and emergency assistance.',
    'home.hero.cta1': 'Consular Services',
    'home.hero.cta2': 'Emergency Assistance',
    'home.hero.support': '24/7 Emergency Support',
    'home.hero.served': '15,000+ Citizens Served',
    'home.hero.quickAccess': 'Quick Access',
    'home.hero.essentialForms': 'Essential Forms',
    'home.hero.formsDesc': 'Download passport, visa, and authentication forms instantly.',
    'home.hero.browseForms': 'Browse Forms',
    
    // Services
    'services.title': 'Consular Services',
    'services.subtitle': 'Comprehensive support for all your needs',
    'services.passport.title': 'Passport Services',
    'services.passport.desc': 'Renewal, issuance, and emergency travel documents',
    'services.civil.title': 'Civil Status Documents',
    'services.civil.desc': 'Birth, marriage, death certificates and registrations',
    'services.auth.title': 'Authentication',
    'services.auth.desc': 'Document legalization and apostille services',
    'services.visa.title': 'Visa Assistance',
    'services.visa.desc': 'Information and support for visa applications',
    'services.notary.title': 'Notary Services',
    'services.notary.desc': 'Power of attorney and legal document certification',
    'services.emergency.title': 'Emergency Support',
    'services.emergency.desc': '24/7 assistance for Lebanese citizens in distress',
    
    // About Embassy
    'about.title': 'About the Embassy',
    'about.subtitle': 'Strengthening Lebanese-Kuwaiti Relations',
    'about.mission.title': 'Our Mission',
    'about.mission.desc': 'To serve and protect the interests of Lebanese citizens in Kuwait while fostering strong diplomatic, economic, and cultural ties between Lebanon and Kuwait.',
    'about.history.title': 'Diplomatic History',
    'about.history.desc': 'Established in 1963, the Lebanese Embassy in Kuwait has been a cornerstone of bilateral relations for over 60 years, supporting thousands of Lebanese citizens and businesses.',
    'about.community.title': 'Community Support',
    'about.community.desc': 'We provide comprehensive consular services, emergency assistance, and cultural programs to serve the vibrant Lebanese community of over 15,000 citizens residing in Kuwait.',
    'about.stats.citizens': 'Lebanese Citizens',
    'about.stats.years': 'Years of Service',
    'about.stats.services': 'Services Provided',
    
    // News
    'news.title': 'Latest News & Updates',
    'news.subtitle': 'Stay informed about embassy activities and announcements',
    'news.featured': 'Featured News',
    'news.readMore': 'Read More',
    'news.viewAll': 'View All News',
    'news.filter.all': 'All',
    'news.filter.events': 'Events',
    'news.filter.services': 'Services',
    'news.filter.announcements': 'Announcements',
    'news.filter.community': 'Community',
    
    // Ambassador
    'ambassador.title': 'His Excellency',
    'ambassador.experience': 'Years of Diplomatic Experience',
    'ambassador.languages': 'Languages Spoken',
    'ambassador.posts': 'Diplomatic Posts',
    'ambassador.message.title': 'Message from the Ambassador',
    'ambassador.activities.title': 'Recent Activities',
    'ambassador.vision.title': 'Vision & Priorities',
    'ambassador.career.title': 'Career Timeline',
    'ambassador.biography.title': 'Biography',
    
    // Forms
    'forms.title': 'Forms & Documents',
    'forms.subtitle': 'Download required forms for consular services',
    'forms.download': 'Download',
    'forms.requirements': 'Requirements',
    'forms.processing': 'Processing Time',
    'forms.categories': 'Categories',
    'forms.all': 'All Forms',
    'forms.instructions.title': 'Submission Instructions',
    'forms.faq.title': 'Frequently Asked Questions',
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Get in touch with our team',
    'contact.general.title': 'General Contact',
    'contact.address': 'Address',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.hours': 'Working Hours',
    'contact.emergency.title': 'Emergency Contact',
    'contact.emergency.desc': '24/7 Emergency Hotline',
    'contact.departments.title': 'Department Contacts',
    'contact.form.title': 'Send us a Message',
    'contact.form.name': 'Full Name',
    'contact.form.email': 'Email Address',
    'contact.form.subject': 'Subject',
    'contact.form.message': 'Message',
    'contact.form.submit': 'Send Message',
    'contact.social.title': 'Follow Us',
    'contact.location.title': 'Location',
    
    // Footer
    'footer.about': 'About',
    'footer.services': 'Services',
    'footer.resources': 'Resources',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'An error occurred',
    'common.learnMore': 'Learn More',
    'common.viewMore': 'View More',
    'common.backToHome': 'Back to Home',
  },
  ar: {
    // Header
    'nav.home': 'الرئيسية',
    'nav.ambassador': 'السفير',
    'nav.news': 'الأخبار',
    'nav.forms': 'النماذج',
    'nav.contact': 'اتصل بنا',
    'nav.admin': 'الإدارة',
    'header.embassy': 'سفارة لبنان',
    'header.location': 'دولة الكويت',
    'header.emergency': 'الخدمات الطارئة',
    'header.search': 'بحث...',
    
    // Homepage
    'home.hero.badge': 'البوابة الحكومية الرسمية',
    'home.hero.title': 'سفارة لبنان',
    'home.hero.titleHighlight': 'في الكويت',
    'home.hero.description': 'نخدم الجالية اللبنانية بتفانٍ وتميز. شريكك الموثوق للخدمات القنصلية والوثائق والمساعدة الطارئة.',
    'home.hero.cta1': 'الخدمات القنصلية',
    'home.hero.cta2': 'المساعدة الطارئة',
    'home.hero.support': 'دعم طوارئ على مدار الساعة',
    'home.hero.served': 'أكثر من 15,000 مواطن تم خدمتهم',
    'home.hero.quickAccess': 'وصول سريع',
    'home.hero.essentialForms': 'النماذج الأساسية',
    'home.hero.formsDesc': 'تحميل نماذج جواز السفر والتأشيرة والتوثيق فوراً.',
    'home.hero.browseForms': 'تصفح النماذج',
    
    // Services
    'services.title': 'الخدمات القنصلية',
    'services.subtitle': 'دعم شامل لجميع احتياجاتك',
    'services.passport.title': 'خدمات جواز السفر',
    'services.passport.desc': 'التجديد والإصدار ووثائق السفر الطارئة',
    'services.civil.title': 'وثائق الأحوال المدنية',
    'services.civil.desc': 'شهادات الميلاد والزواج والوفاة والتسجيلات',
    'services.auth.title': 'التوثيق',
    'services.auth.desc': 'تصديق الوثائق وخدمات الأبوستيل',
    'services.visa.title': 'المساعدة في التأشيرات',
    'services.visa.desc': 'معلومات ودعم لطلبات التأشيرة',
    'services.notary.title': 'الخدمات الكاتب العدل',
    'services.notary.desc': 'التوكيل الرسمي وتصديق الوثائق القانونية',
    'services.emergency.title': 'الدعم الطارئ',
    'services.emergency.desc': 'مساعدة على مدار الساعة للمواطنين اللبنانيين في محنة',
    
    // About Embassy
    'about.title': 'عن السفارة',
    'about.subtitle': 'تعزيز العلاقات اللبنانية الكويتية',
    'about.mission.title': 'مهمتنا',
    'about.mission.desc': 'خدمة وحماية مصالح المواطنين اللبنانيين في الكويت مع تعزيز العلاقات الدبلوماسية والاقتصادية والثقافية القوية بين لبنان والكويت.',
    'about.history.title': 'التاريخ الدبلوماسي',
    'about.history.desc': 'تأسست سفارة لبنان في الكويت عام 1963، وكانت حجر الزاوية في العلاقات الثنائية لأكثر من 60 عاماً، حيث دعمت آلاف المواطنين اللبنانيين والشركات.',
    'about.community.title': 'دعم المجتمع',
    'about.community.desc': 'نقدم خدمات قنصلية شاملة ومساعدة طارئة وبرامج ثقافية لخدمة الجالية اللبنانية النابضة بالحياة التي تضم أكثر من 15,000 مواطن مقيم في الكويت.',
    'about.stats.citizens': 'مواطن لبناني',
    'about.stats.years': 'سنة من الخدمة',
    'about.stats.services': 'خدمة مقدمة',
    
    // News
    'news.title': 'آخر الأخبار والتحديثات',
    'news.subtitle': 'ابق على اطلاع بأنشطة السفارة والإعلانات',
    'news.featured': 'الأخبار المميزة',
    'news.readMore': 'اقرأ المزيد',
    'news.viewAll': 'عرض جميع الأخبار',
    'news.filter.all': 'الكل',
    'news.filter.events': 'الفعاليات',
    'news.filter.services': 'الخدمات',
    'news.filter.announcements': 'الإعلانات',
    'news.filter.community': 'المجتمع',
    
    // Ambassador
    'ambassador.title': 'سعادة السفير',
    'ambassador.experience': 'سنوات من الخبرة الدبلوماسية',
    'ambassador.languages': 'اللغات المنطوقة',
    'ambassador.posts': 'المناصب الدبلوماسية',
    'ambassador.message.title': 'رسالة من السفير',
    'ambassador.activities.title': 'الأنشطة الأخيرة',
    'ambassador.vision.title': 'الرؤية والأولويات',
    'ambassador.career.title': 'المسار الوظيفي',
    'ambassador.biography.title': 'السيرة الذاتية',
    
    // Forms
    'forms.title': 'النماذج والوثائق',
    'forms.subtitle': 'تحميل النماذج المطلوبة للخدمات القنصلية',
    'forms.download': 'تحميل',
    'forms.requirements': 'المتطلبات',
    'forms.processing': 'وقت المعالجة',
    'forms.categories': 'الفئات',
    'forms.all': 'جميع النماذج',
    'forms.instructions.title': 'تعليمات التقديم',
    'forms.faq.title': 'الأسئلة الشائعة',
    
    // Contact
    'contact.title': 'اتصل بنا',
    'contact.subtitle': 'تواصل مع فريقنا',
    'contact.general.title': 'الاتصال العام',
    'contact.address': 'العنوان',
    'contact.phone': 'الهاتف',
    'contact.email': 'البريد الإلكتروني',
    'contact.hours': 'ساعات العمل',
    'contact.emergency.title': 'الاتصال الطارئ',
    'contact.emergency.desc': 'خط ساخن للطوارئ على مدار الساعة',
    'contact.departments.title': 'اتصالات الأقسام',
    'contact.form.title': 'أرسل لنا رسالة',
    'contact.form.name': 'الاسم الكامل',
    'contact.form.email': 'عنوان البريد الإلكتروني',
    'contact.form.subject': 'الموضوع',
    'contact.form.message': 'الرسالة',
    'contact.form.submit': 'إرسال الرسالة',
    'contact.social.title': 'تابعنا',
    'contact.location.title': 'الموقع',
    
    // Footer
    'footer.about': 'عن',
    'footer.services': 'الخدمات',
    'footer.resources': 'الموارد',
    'footer.contact': 'اتصل',
    'footer.rights': 'جميع الحقوق محفوظة',
    
    // Common
    'common.loading': 'جاري التحميل...',
    'common.error': 'حدث خطأ',
    'common.learnMore': 'اعرف المزيد',
    'common.viewMore': 'عرض المزيد',
    'common.backToHome': 'العودة إلى الصفحة الرئيسية',
  },
};